import type { ReactNode } from "react";
import { BookOpen, ExternalLink } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Button, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@ncp/ui";

export interface HelpTopic {
  title: string;
  body: ReactNode;
  /** Anchor inside docs/admin-guide.md (served from /docs/admin-guide.html in production). */
  docsAnchor?: string;
}

/** Context help for the current screen: what it does, how changes reach production and where the administrator guide covers it. */
export function HelpDrawer({ open, onOpenChange, topic }: { open: boolean; onOpenChange: (o: boolean) => void; topic: HelpTopic | null }) {
  const { t } = useTranslation();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="end" width="max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="size-4 text-aegold-700" /> {topic?.title ?? t("admin:shell.help")}
          </SheetTitle>
          <SheetDescription>{t("admin:shell.helpHint")}</SheetDescription>
        </SheetHeader>
        <SheetBody className="prose prose-sm max-w-none text-sm leading-relaxed">
          {topic?.body ?? <p>{t("admin:shell.helpHint")}</p>}
          <div className="mt-6 not-prose">
            <Button asChild variant="outline" size="sm">
              <a href={`${import.meta.env.BASE_URL}docs/admin-guide.html${topic?.docsAnchor ? `#${topic.docsAnchor}` : ""}`} target="_blank" rel="noopener">
                <ExternalLink /> {t("admin:app.docs")}
              </a>
            </Button>
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
